import { Module } from 'vuex';
import { RootState } from '..';
import { Route } from 'vue-router';

const SET_TRANSITION_DIRECTION = 'SET_TRANSITION_DIRECTION';
const SET_NAVIGATION_TYPE = 'SET_NAVIGATION_TYPE';
const SET_PANEL_HEIGHT = 'SET_PANEL_HEIGHT';
const RESET_STATE = 'RESET_STATE';

export enum PageType {
  Overview = 'overview',
  Outside = 'outside',
  Inside = 'inside'
}

export enum TransitionDirection {
  Back = 'back',
  Forward = 'forward'
}

export enum TransitionColor {
  White = 'white',
  Gray = 'gray'
}

export enum NavigationType {
  OverviewOutside = 'overview-outside',
  OverviewInside = 'overview-inside',
  OverviewOverview = 'overview-overview',

  OutsideOutside = 'outside-outside',
  OutsideInside = 'outside-inside',
  OutsideOverview = 'outside-overview',

  InsideOutside = 'inside-outside',
  InsideInside = 'inside-inside',
  InsideOverview = 'inside-overview'
}

export enum TransitionMode {
  Background,
  PanelHeight,
  PanelTranslate
}

export interface TransitionsState {
  direction: TransitionDirection;
  navigationType: NavigationType;
  panelHeight: number;
}

const transitionsModuleInitialState = (): TransitionsState => ({
  direction: TransitionDirection.Back,
  navigationType: NavigationType.OverviewOutside,
  panelHeight: 0
});

export const transitionsModule: Module<TransitionsState, RootState> = {
  namespaced: true,
  state: transitionsModuleInitialState(),
  getters: {
    transitionMode(state): TransitionMode {
      let type: TransitionMode;

      switch (state.navigationType) {
        case NavigationType.OutsideOutside: {
          type = TransitionMode.PanelHeight;
          break;
        }
        case NavigationType.InsideInside: {
          type = TransitionMode.PanelTranslate;
          break;
        }
        default: {
          type = TransitionMode.Background;
        }
      }

      return type;
    },
    transitionColor(state): TransitionColor {
      return [NavigationType.OverviewInside, NavigationType.OutsideInside].includes(state.navigationType)
        ? TransitionColor.Gray
        : TransitionColor.White;
    }
  },
  actions: {
    setNavigationType({ commit, rootState, getters }, navigation: { to: Route; from: Route }) {
      const to: PageType = navigation.to.meta.type || PageType.Overview;
      const from: PageType = navigation.from.meta.type || PageType.Overview;

      const routeToIndex: number = rootState.app.processRoutes.findIndex(r => r.name === navigation.to.name);
      const routeFromIndex: number = rootState.app.processRoutes.findIndex(r => r.name === navigation.from.name);

      const transitionDirection: TransitionDirection =
        routeFromIndex <= routeToIndex ? TransitionDirection.Forward : TransitionDirection.Back;

      commit(SET_NAVIGATION_TYPE, `${from}-${to}`);
      commit(SET_TRANSITION_DIRECTION, transitionDirection);

      return {
        mode: getters.transitionMode,
        direction: transitionDirection
      };
    },
    setPanelHeight({ commit }, height: number) {
      commit(SET_PANEL_HEIGHT, height);
    },
    resetState({ commit }) {
      commit(RESET_STATE);
    }
  },
  mutations: {
    [SET_NAVIGATION_TYPE](state, navigationType: NavigationType) {
      state.navigationType = navigationType;
    },
    [SET_TRANSITION_DIRECTION](state, direction: TransitionDirection) {
      state.direction = direction;
    },
    [SET_PANEL_HEIGHT](state, height: number) {
      state.panelHeight = height;
    },
    [RESET_STATE](state) {
      Object.assign(state, transitionsModuleInitialState());
    }
  }
};
